import { useCallback, useMemo } from 'react';
import type { FC } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import classNames from 'classnames';

import {
  changeComposeVisibility,
  setComposeQuotePolicy,
} from '@/mastodon/actions/compose_typed';
import { openModal } from '@/mastodon/actions/modal';
import type { ApiQuotePolicy } from '@/mastodon/api_types/quotes';
import type { StatusVisibility } from '@/mastodon/api_types/statuses';
import { Icon } from '@/mastodon/components/icon';
import { useAppSelector, useAppDispatch } from '@/mastodon/store';
import AlternateEmailIcon from '@/material-icons/400-24px/alternate_email.svg?react';
import LockIcon from '@/material-icons/400-24px/lock.svg?react';
import PublicIcon from '@/material-icons/400-24px/public.svg?react';
import QuietTimeIcon from '@/material-icons/400-24px/quiet_time.svg?react';

import type { VisibilityModalCallback } from '../../ui/components/visibility_modal';

import { messages as privacyMessages } from './privacy_dropdown';

const messages = defineMessages({
  anyone_quote: {
    id: 'privacy.quote.anyone',
    defaultMessage: '{visibility}, anyone can quote',
  },
  limited_quote: {
    id: 'privacy.quote.limited',
    defaultMessage: '{visibility}, quotes limited',
  },
  disabled_quote: {
    id: 'privacy.quote.disabled',
    defaultMessage: '{visibility}, quotes disabled',
  },
  audience_limited: {
    id: 'privacy.audience_limited',
    defaultMessage: 'Visible to {audience} only',
  },
  audience_limited_generic: {
    id: 'privacy.audience_limited_generic',
    defaultMessage: 'Visible to a limited audience only',
  },
  private_group_inherited: {
    id: 'privacy.private_group_inherited',
    defaultMessage: 'Private; inherited',
  },
  list_limited: {
    id: 'privacy.list_limited',
    defaultMessage: 'Visible to {list} only',
  },
  list_limited_generic: {
    id: 'privacy.list_limited_generic',
    defaultMessage: 'Visible to a limited list only',
  },
});

interface PrivacyDropdownProps {
  disabled?: boolean;
}

export const VisibilityButton: FC<PrivacyDropdownProps> = (props) => {
  return <PrivacyModalButton {...props} />;
};

const visibilityOptions = {
  public: {
    icon: 'globe',
    iconComponent: PublicIcon,
    value: 'public',
    text: privacyMessages.public_short,
  },
  unlisted: {
    icon: 'unlock',
    iconComponent: QuietTimeIcon,
    value: 'unlisted',
    text: privacyMessages.unlisted_short,
  },
  private: {
    icon: 'lock',
    iconComponent: LockIcon,
    value: 'private',
    text: privacyMessages.private_short,
  },
  direct: {
    icon: 'at',
    iconComponent: AlternateEmailIcon,
    value: 'direct',
    text: privacyMessages.direct_short,
  },
};

const PrivacyModalButton: FC<PrivacyDropdownProps> = ({ disabled = false }) => {
  const intl = useIntl();

  const quotePolicy = useAppSelector(
    (state) => state.compose.get('quote_policy') as ApiQuotePolicy,
  );
  const visibility = useAppSelector(
    (state) => state.compose.get('privacy') as StatusVisibility,
  );
  const audienceListId = useAppSelector(
    (state) => state.compose.get('audience_list_id') as string | null,
  );
  const audienceList = useAppSelector((state) =>
    audienceListId
      ? state.audienceLists.audienceLists[audienceListId]
      : undefined,
  );
  const listId = useAppSelector(
    (state) => state.compose.get('list_id') as string | null,
  );
  const list = useAppSelector((state) =>
    listId ? (state.lists.get(listId) ?? undefined) : undefined,
  );
  const groupId = useAppSelector(
    (state) => state.compose.get('group_id') as string | null,
  );
  // A private group's own privacy already gates who can ever see the post
  // (StatusPolicy#show?), regardless of the stored visibility value -- so
  // "Public"/"Quiet public" there would be misleading, and the server
  // forces it to private anyway. Lock the control and show that plainly
  // instead of letting it imply a broader audience than is actually
  // possible.
  const isPrivateGroupPost = useAppSelector((state) =>
    groupId ? !!state.groups.groups[groupId]?.private : false,
  );

  const { icon, iconComponent } = useMemo(() => {
    if (audienceListId || listId || isPrivateGroupPost) {
      return { icon: 'lock', iconComponent: LockIcon };
    }
    const option = visibilityOptions[visibility];
    return { icon: option.icon, iconComponent: option.iconComponent };
  }, [visibility, audienceListId, listId, isPrivateGroupPost]);
  const text = useMemo(() => {
    if (audienceListId) {
      // A reply to someone else's audience-limited post inherits that
      // audience too, but only the list's owner can see its name (an
      // unprivileged member replying doesn't have it in their own audience
      // list store) -- fall back to a generic label in that case.
      return audienceList
        ? intl.formatMessage(messages.audience_limited, {
            audience: audienceList.name,
          })
        : intl.formatMessage(messages.audience_limited_generic);
    }
    if (listId) {
      // Same reasoning as audienceList above -- a reply-inherited List
      // isn't necessarily one of your own, so it may not be in your own
      // lists store.
      return list
        ? intl.formatMessage(messages.list_limited, { list: list.title })
        : intl.formatMessage(messages.list_limited_generic);
    }
    if (isPrivateGroupPost) {
      return intl.formatMessage(messages.private_group_inherited);
    }
    const visibilityText = intl.formatMessage(
      visibilityOptions[visibility].text,
    );
    if (visibility === 'private' || visibility === 'direct') {
      return visibilityText;
    }
    if (quotePolicy === 'nobody') {
      return intl.formatMessage(messages.disabled_quote, {
        visibility: visibilityText,
      });
    }
    if (quotePolicy !== 'public') {
      return intl.formatMessage(messages.limited_quote, {
        visibility: visibilityText,
      });
    }
    return intl.formatMessage(messages.anyone_quote, {
      visibility: visibilityText,
    });
  }, [
    quotePolicy,
    visibility,
    intl,
    audienceListId,
    audienceList,
    listId,
    list,
    isPrivateGroupPost,
  ]);

  const dispatch = useAppDispatch();

  const handleChange: VisibilityModalCallback = useCallback(
    (newVisibility, newQuotePolicy) => {
      if (newVisibility !== visibility) {
        dispatch(changeComposeVisibility(newVisibility));
      }
      if (newQuotePolicy !== quotePolicy) {
        dispatch(setComposeQuotePolicy(newQuotePolicy));
      }
    },
    [dispatch, quotePolicy, visibility],
  );

  const handleOpen = useCallback(() => {
    dispatch(
      openModal({
        modalType: 'COMPOSE_PRIVACY',
        modalProps: { onChange: handleChange },
      }),
    );
  }, [dispatch, handleChange]);

  return (
    <button
      type='button'
      title={intl.formatMessage(privacyMessages.change_privacy)}
      onClick={handleOpen}
      disabled={disabled || !!audienceListId || !!listId || isPrivateGroupPost}
      className={classNames('dropdown-button', 'visibility-button')}
    >
      <Icon id={icon} icon={iconComponent} />
      <span className='dropdown-button__label'>{text}</span>
    </button>
  );
};
